<template>
    <v-main>
        <v-container class="exam">
            <!-- Exam Header -->
            <v-card class="exam__header" elevation="0">
                <div class="exam__header-info">
                    <h1 class="exam__title">{{ courses.length ? courses[0].name : '' }}</h1>
                    <p class="exam__instructions">
                        <v-icon small color="#0E8390">mdi-information-outline</v-icon>
                        Read each question and select the correct answer. The exam submits automatically when time runs out.
                    </p>
                </div>
                <div class="exam__timer">
                    <v-icon color="#EAF1F5" small>mdi-timer-outline</v-icon>
                    <span class="exam__timer-value">{{ formattedTime }}</span>
                    <span class="exam__timer-label">remaining</span>
                </div>
            </v-card>

            <!-- Question Section -->
            <v-card v-for="(question, index) in shuffledQuestions" :key="index" class="exam__q" elevation="0">
                <div class="exam__q-head">
                    <span class="exam__q-num">Q{{ index + 1 }}</span>
                    <h2 class="exam__q-text" v-html="question.question"></h2>
                </div>
                <v-row class="exam__opts" dense>
                    <v-col cols="12" sm="6">
                        <div class="exam__opt">
                            <v-checkbox hide-details :label="question.option_1" :value="question.option_1"
                                v-model="selectedOptions[index]"></v-checkbox>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <div class="exam__opt">
                            <v-checkbox hide-details :label="question.option_2" :value="question.option_2"
                                v-model="selectedOptions[index]"></v-checkbox>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <div class="exam__opt">
                            <v-checkbox hide-details :label="question.option_3" :value="question.option_3"
                                v-model="selectedOptions[index]"></v-checkbox>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <div class="exam__opt">
                            <v-checkbox hide-details :label="question.option_4" :value="question.option_4"
                                v-model="selectedOptions[index]"></v-checkbox>
                        </div>
                    </v-col>
                </v-row>
            </v-card>

            <!-- Exam Navigation Buttons -->
            <div class="exam__submit">
                <v-progress-circular v-if="isLoading" indeterminate color="primary"></v-progress-circular>
                <v-btn color="primary" v-else x-large @click="openSubmitDialog">
                    <v-icon left>mdi-check-circle-outline</v-icon>Submit exam
                </v-btn>
            </div>
        </v-container>
        <v-dialog v-model="dialog" max-width="400">
            <v-card>
                <v-card-title class="headline">Confirm Submission</v-card-title>
                <v-card-text>
                    Are you sure you want to submit the exam?
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" @click="dialog = false">Cancel</v-btn>
                    <v-btn color="primary" dark @click="submitExam">Submit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Result Dialog -->
        <v-dialog v-if="result != null" v-model="resultDialog" max-width="420">
            <v-card class="exam-result">
                <div class="exam-result__banner" :class="exam_pass ? 'is-pass' : 'is-fail'">
                    <v-icon size="52" color="#ffffff">{{ exam_pass ? 'mdi-check-decagram' : 'mdi-emoticon-sad-outline' }}</v-icon>
                    <h2 class="exam-result__title">{{ exam_pass ? 'Congratulations, you passed!' : 'Not passed this time' }}</h2>
                    <p class="exam-result__course">{{ courses.length ? courses[0].name : '' }}</p>
                </div>

                <div class="exam-result__body">
                    <v-progress-circular
                        :value="Number(score_percentage) || 0"
                        :size="128" :width="12"
                        :color="exam_pass ? '#2E8B6F' : '#C0453B'"
                        class="exam-result__ring">
                        <span class="exam-result__pct">{{ score_percentage }}%</span>
                    </v-progress-circular>

                    <div class="exam-result__stats">
                        <div class="exam-result__stat">
                            <span class="exam-result__stat-label">Marks scored</span>
                            <span class="exam-result__stat-value">{{ score_marks }}</span>
                        </div>
                        <div class="exam-result__stat">
                            <span class="exam-result__stat-label">Passing mark</span>
                            <span class="exam-result__stat-value">{{ passing_marks }}%</span>
                        </div>
                    </div>

                    <p class="exam-result__msg">
                        {{ exam_pass
                            ? 'Your certificate is ready to download.'
                            : 'Review the course material and try again when you are ready.' }}
                    </p>
                </div>

                <v-card-actions class="exam-result__actions">
                    <v-btn v-if="exam_pass" color="success" large
                        @click="downloadCertificate" :loading="isDownloading" :disabled="isDownloading">
                        <v-icon left>mdi-download</v-icon>
                        {{ isDownloading ? 'Generating…' : 'Download Certificate' }}
                    </v-btn>
                    <v-btn color="primary" :text="exam_pass" large @click="CertificateGenerate">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-main>
</template>
<script>
import axios from "axios";
import { generateCertificatePDF, formatCertificateDate } from "@/services/certificatePdfService";

export default {
    name: "CourseExam",
    data() {
        return {
            questions: [],
            selectedOptions: [],
            courses: [],
            shuffledQuestions: [],
            examDuration: 0,
            timer: null,
            dialog: false,
            resultDialog: false,
            result: '',
            passing_marks: '',
            score_marks: '',
            score_percentage: '',
            exam_pass: false,
            isRefreshing: false,
            reloadCount: 0,
            storedSeconds: '',
            storedMinutes: '',
            isExamSubmitted: false,
            isDownloading: false,
            isLoading: false,
        };
    },
    computed: {
        formattedTime() {
            const minutes = Math.floor(this.examDuration / 60);
            const seconds = this.examDuration % 60;
            if (this.storedMinutes !== '' && this.storedSeconds !== '') {
                return `${this.storedMinutes}:${this.storedSeconds < 10 ? '0' : ''}${this.storedSeconds}`;
            } else {
                return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
            }
        },
    },
    mounted() {
        this.disableBackButton();
        window.addEventListener("beforeunload", this.beforeUnloadHandler);
        window.addEventListener("visibilitychange", this.handleVisibilityChange);
        const storedTime = localStorage.getItem('remainingTime');
        if (storedTime) {
            const [storedMinutes, storedSeconds] = storedTime.split(':');
            this.storedMinutes = storedMinutes;
            this.storedSeconds = storedSeconds;
        }
        const examSubmitted = localStorage.getItem('examSubmitted');
        if (examSubmitted === 'true') {
            localStorage.removeItem('examSubmitted');
            this.$router.push('/course');
        } else {
            this.getData();
        }
    },
    beforeDestroy() {
        window.onbeforeunload = null;
        this.enableBackButton();
        window.removeEventListener("visibilitychange", this.handleVisibilityChange);
        localStorage.removeItem('remainingTime');
        localStorage.removeItem('examSubmitted');
        clearInterval(this.timer);
    },
    // beforeRouteLeave(to, from, next) {
    //     if (localStorage.getItem('examSubmitted') !== 'true') {
    //         const confirmMessage = "if you Leave the page exam will submit automatically. Are you sure you want to submit?";
    //         if (window.confirm(confirmMessage)) {
    //             localStorage.removeItem('remainingTime');
    //             clearInterval(this.timer);
    //             this.submitExam(); // Submit the exam API
    //             next();
    //         } else {
    //             next(false);
    //         }
    //     } else {
    //         next();
    //     }
    // },
    methods: {
        confirmLeave(event) {
            const confirmationMessage = "Are you sure you want to leave?";
            event.returnValue = confirmationMessage;
            return confirmationMessage;
        },
        CertificateGenerate() {
            if (this.exam_pass == true) {
                this.$router.push(`/course`);

            } else {
                this.$router.push(`/course`);
            }
        },
        async downloadCertificate() {
            if (this.isDownloading) return;

            this.isDownloading = true;

            try {
                const course = this.courses[0];

                // Build certificate data
                const certificateData = {
                    candidateName: `${this.user.name || ''} ${this.user.last_name || ''}`.trim(),
                    cdcNumber: this.user.cdc || 'N/A',
                    courseName: course?.name || '',
                    completionDate: formatCertificateDate(new Date()),
                    presentedDate: formatCertificateDate(new Date()),
                    validityPeriod: course?.validity_period || '12 months',
                    certNo: ''
                };

                // Generate PDF using frontend service
                await generateCertificatePDF(certificateData);

            } catch (error) {
                console.error('Certificate download failed:', error);
                alert('Failed to download certificate. Please try again.');
            } finally {
                this.isDownloading = false;
            }
        },
        async submitExam() {
            if (this.isLoading) return; // guard against double-submit
            this.isLoading = true;
            this.dialog = false; // close the confirm dialog
            const ExamSelectoption = this.shuffledQuestions.map((question, index) => ({
                question_id: question.id,
                selected_option: this.selectedOptions[index] || '', // Access the selected option
                marks: question.marks, // Access the marks
            }));
            let payload = {
                course_id: this.$route.params.courseId,
                candidate_id: this.user.id,
                selected: ExamSelectoption,
            }
            try {
                let response = await axios.post(`/course_exams`, payload);
                this.passing_marks = response.data.passing_marks;
                this.score_marks = response.data.score_marks;
                this.score_percentage = response.data.score_percentage;

                if (this.score_percentage >= this.passing_marks) {
                    this.exam_pass = true
                } else {
                    this.exam_pass = false
                }
                this.isExamSubmitted = true;
                localStorage.setItem('examSubmitted', 'true');
                this.resultDialog = true;
            } catch (e) {
                // Surface the real reason instead of silently failing (e.g. the
                // server rejecting with "no remaining attempts"). The attempt is
                // NOT consumed when submission fails.
                const msg =
                    (e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
                    'Could not submit the exam. Please try again.';
                alert(msg);
            } finally {
                this.isLoading = false;
            }
        },
        disableBackButton() {
            window.history.pushState(null, document.title, window.location.href);

            window.onpopstate = function () {
                window.history.pushState(null, document.title, window.location.href);
            };
        },
        enableBackButton() {
            window.onpopstate = null;
        },
        openSubmitDialog() {
            this.dialog = true;
        },
        closeSubmitDialog() {
            this.dialog = false;
        },
        handleVisibilityChange() {
            if (document.visibilityState === "hidden") {
                console.log('submite')
            }
        },
        async getData() {
            this.isLoading = true;
            let questions = await axios.get(`/course_questions?course_id=${this.$route.params.courseId}&is_exam=true`);
            this.questions = questions.data.data;
            this.courses = questions.data.data.map(item => item.course);
            // Get the number of questions to display from the course
            const numberOfQuestions = parseInt(this.courses[0].no_of_quiz);
            // Get the exam duration in seconds
            this.examDuration = parseInt(this.courses[0].exam_duration) * 60;
            // Shuffle the questions
            this.shuffledQuestions = this.shuffle(this.questions).slice(0, numberOfQuestions);
            if (this.storedMinutes !== '' && this.storedSeconds !== '') {
                this.examDuration = parseInt(this.storedMinutes) * 60 + parseInt(this.storedSeconds);
            }
            this.timer = setInterval(() => {
                if (this.examDuration > 0) {
                    this.examDuration--;
                    this.saveRemainingTime();
                } else {
                    this.submitExam();
                    clearInterval(this.timer);
                }
            }, 1000);
            this.isLoading = false;
        },
        saveRemainingTime() {
            const minutes = Math.floor(this.examDuration / 60);
            const seconds = this.examDuration % 60;
            this.storedMinutes = minutes;
            this.storedSeconds = seconds;
            localStorage.setItem('remainingTime', `${minutes}:${seconds}`);
        },
        // Function to shuffle an array
        shuffle(array) {
            let currentIndex = array.length, randomIndex;

            while (currentIndex !== 0) {
                randomIndex = Math.floor(Math.random() * currentIndex);
                currentIndex--;

                [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
            }

            return array;
        },

    },
};
</script>
<style scoped>
.certificate-generate {
    background-color: #1976d2;
    color: white;
    padding: 8px;
    text-decoration: none;
    border-radius: 8px;
}

.exam { max-width: 860px; }

/* ---- header ---- */
.exam__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 20px 24px;
    border-radius: 14px !important;
    border: 1px solid #dbe3e8;
    background: #ffffff;
    margin-bottom: 20px;
}
.exam__title {
    font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #0c3a5b;
    line-height: 1.2;
    margin: 0;
}
.exam__instructions {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #61798a;
    font-size: 0.86rem;
    margin: 6px 0 0;
}
.exam__timer {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    padding: 10px 18px;
    border-radius: 12px;
    background: linear-gradient(135deg, #08263c, #0c3a5b 60%, #0e5f73);
    color: #eaf1f5;
    box-shadow: 0 6px 16px rgba(8, 38, 60, 0.25);
}
.exam__timer-value {
    font-family: 'Bahnschrift', 'Consolas', monospace;
    font-size: 1.9rem;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
}
.exam__timer-label {
    font-size: 0.66rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    opacity: 0.75;
}

/* ---- question cards ---- */
.exam__q {
    padding: 22px 24px;
    border-radius: 14px !important;
    border: 1px solid #dbe3e8;
    background: #ffffff;
    margin-bottom: 16px;
}
.exam__q-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
}
.exam__q-num {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    background: rgba(12, 58, 91, 0.08);
    color: #0c3a5b;
    font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
    font-weight: 700;
    font-size: 0.9rem;
    box-shadow: inset 3px 0 0 #D69A34;
}
.exam__q-text {
    font-size: 1.06rem;
    font-weight: 600;
    color: #132430;
    line-height: 1.4;
    margin: 2px 0 0;
}

/* ---- options as tiles ---- */
.exam__opts { margin-top: 4px; }
.exam__opt {
    border: 1px solid #e2e8ec;
    border-radius: 10px;
    padding: 4px 12px;
    transition: border-color .15s ease, background .15s ease;
    height: 100%;
}
.exam__opt:hover { border-color: #0E8390; background: rgba(14, 131, 144, 0.04); }
.exam__opt >>> .v-input--selection-controls { margin-top: 6px; padding-top: 0; }
.exam__opt >>> .v-label { color: #132430; font-size: 0.95rem; }

/* ---- submit ---- */
.exam__submit {
    display: flex;
    justify-content: flex-end;
    margin: 24px 0 60px;
}

/* ---- result dialog ---- */
.exam-result { border-radius: 16px !important; overflow: hidden; }
.exam-result__banner {
    text-align: center;
    padding: 26px 20px 20px;
    color: #ffffff;
}
.exam-result__banner.is-pass { background: linear-gradient(135deg, #2E8B6F, #37a884); }
.exam-result__banner.is-fail { background: linear-gradient(135deg, #C0453B, #d8695f); }
.exam-result__title {
    font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    margin: 10px 0 2px;
    color: #ffffff;
    line-height: 1.25;
}
.exam-result__course { font-size: 0.85rem; opacity: 0.92; margin: 0; }

.exam-result__body { padding: 24px 24px 6px; text-align: center; }
.exam-result__ring { margin-bottom: 18px; }
.exam-result__pct {
    font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #132430;
    font-variant-numeric: tabular-nums;
}
.exam-result__stats { display: flex; gap: 12px; justify-content: center; margin-bottom: 14px; }
.exam-result__stat {
    flex: 1;
    max-width: 150px;
    background: #f2f6f8;
    border: 1px solid #e2e8ec;
    border-radius: 10px;
    padding: 10px 8px;
}
.exam-result__stat-label {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #61798a;
}
.exam-result__stat-value {
    display: block;
    font-family: 'Bahnschrift', 'Segoe UI', system-ui, sans-serif;
    font-size: 1.3rem;
    font-weight: 700;
    color: #0c3a5b;
    margin-top: 2px;
}
.exam-result__msg { font-size: 0.88rem; color: #61798a; margin: 0; }
.exam-result__actions { justify-content: center; padding: 16px 24px 24px; gap: 8px; }
</style>
  